import { useRef, useState } from 'react';
import { createSimpleHttpCache } from './cache';
import { ExpectedParameterType, FetchRequestParameters, RequestState, ReturnType } from './types';

export * from './types';

function cleanParametersObj(parameters: any) {
  // Remove any null/undefined values from the parameters object
  return Object.fromEntries(
    Object.entries(parameters).filter(([key, value]) => value !== null && value !== undefined)
  ) as { [key: string]: any };
}

export function createFetcher(baseUrl: string) {
  return function useFetchRequest<TResult = any, TParameters extends ExpectedParameterType = any>({
    endpoint,
    method,
    useSimpleCache,
  }: FetchRequestParameters): ReturnType<TResult, TParameters> {
    const [state, setRequestState] = useState<RequestState>('init');

    const cacheRef = useRef(createSimpleHttpCache<TResult, TParameters>());
    const { getCacheEntry, setCacheEntry, getCacheKey } = cacheRef.current;

    const [result, setResult] = useState<TResult>();

    async function performFetch(parameters: TParameters) {
      const cacheKey = getCacheKey({ endpoint, method }, parameters);
      if (useSimpleCache) {
        const cacheEntry = getCacheEntry(cacheKey);
        if (cacheEntry) {
          setResult(cacheEntry);
          setRequestState('ready');
          return null;
        }
      }

      setResult(undefined);

      setRequestState('loading');

      const cleanedParams = cleanParametersObj(parameters);

      let searchParams = method === 'GET' ? `?${new URLSearchParams(cleanedParams)}` : '';
      const res = await fetch(`${baseUrl}${endpoint}${searchParams}`, {
        method,
        body: method === 'POST' ? JSON.stringify(parameters) : undefined,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!res.status.toString().startsWith('2')) {
        setRequestState('error');
        return null;
      }
      try {
        const data = await res.json();
        setResult(data);
        if (useSimpleCache) {
          setCacheEntry(cacheKey, data);
        }
        setRequestState('ready');

        return data as TResult;
      } catch (e) {
        setRequestState('error');
      }

      return null;
    }

    return [
      performFetch,
      {
        state,
        result,
      },
    ];
  };
}
